import React from 'react';
import { Box, Group, Text, Tooltip } from '@cowbell/ui';

import {
  AI_FACTOR_CEILING,
  AI_FACTOR_FLOOR,
  type AiFactorDescriptor,
  getFactorStatus,
} from './aiFactors.model';
import {
  AI_TOOLTIP_STYLE,
  AXIS_TICK_FONT_SIZE,
  FACTOR_LABEL_FONT_SIZE,
  FACTOR_LABEL_FONT_WEIGHT,
  FACTOR_SCORE_FONT_SIZE,
  FACTOR_STATUS_FONT_SIZE,
  FOOTER_FONT_SIZE,
  TILE_SUBTITLE_FONT_SIZE,
  rem,
} from './aiPanelLayout';
import classes from './AiCowbellFactorsPanel.module.css';

interface AiFactorTrackProps {
  readonly factor: AiFactorDescriptor;
  readonly score: number;
  readonly delta: number | null;
}

const TICKS = [
  { value: AI_FACTOR_FLOOR, className: classes.tickFloor },
  { value: AI_FACTOR_CEILING, className: classes.tickCeiling },
];

const getDeltaColor = (delta: number) => {
  if (delta === 0) {
    return 'dimmed';
  }
  return delta > 0 ? 'green.7' : 'red.6';
};

export const AiFactorTrack = ({ factor, score, delta }: AiFactorTrackProps) => {
  const fill = {
    '--ai-factor-fill': `${Math.max(0, Math.min(100, score))}%`,
  } as React.CSSProperties;

  return (
    <Box style={fill}>
      <Group gap={rem(6)}>
        <Text fw={FACTOR_LABEL_FONT_WEIGHT} fz={FACTOR_LABEL_FONT_SIZE}>
          {factor.label}
        </Text>
        <Tooltip
          classNames={{ tooltip: classes.tooltip }}
          label={
            <>
              <Text fw={FACTOR_LABEL_FONT_WEIGHT} fz={TILE_SUBTITLE_FONT_SIZE}>
                {factor.question}
              </Text>
              <Text fz={FOOTER_FONT_SIZE} mt={rem(6)}>
                {factor.explanation}
              </Text>
            </>
          }
          multiline
          position="top"
          style={AI_TOOLTIP_STYLE}
          w={rem(340)}
          withArrow
        >
          <Box
            alt={`${factor.label} information`}
            component="img"
            h={rem(14)}
            src="/images/Tooltip.svg"
            w={rem(14)}
          />
        </Tooltip>
        <Text c="dimmed" fz={FACTOR_STATUS_FONT_SIZE}>
          {getFactorStatus(score, factor.statusWords)}
        </Text>
        {delta !== null && (
          <Text
            c={getDeltaColor(delta)}
            fw={FACTOR_LABEL_FONT_WEIGHT}
            fz={FACTOR_STATUS_FONT_SIZE}
            ml="auto"
          >
            {`${delta > 0 ? '+' : ''}${delta} vs. last run`}
          </Text>
        )}
      </Group>
      <Box className={classes.scoreRow}>
        <Text
          className={classes.scoreValue}
          fw={FACTOR_LABEL_FONT_WEIGHT}
          fz={FACTOR_SCORE_FONT_SIZE}
        >
          {score}
        </Text>
      </Box>
      <Box
        aria-label={`${factor.label} score`}
        aria-valuenow={score}
        className={classes.track}
        role="progressbar"
      >
        <Box bg={factor.color} className={classes.fill} />
        {TICKS.map((tick) => (
          <Box
            className={`${classes.tick} ${tick.className}`}
            key={tick.value}
          />
        ))}
      </Box>
      <Box aria-hidden className={classes.tickLabels}>
        {TICKS.map((tick) => (
          <Text
            c="dimmed"
            className={`${classes.tickLabel} ${tick.className}`}
            fz={AXIS_TICK_FONT_SIZE}
            key={tick.value}
          >
            {tick.value}
          </Text>
        ))}
      </Box>
    </Box>
  );
};
